import { useEffect, useRef, useState } from 'react'
import { ReactSVG } from 'react-svg'
import { SwitchTransition } from 'react-transition-group'
import cn from 'classnames'
import { getImage } from '../../../../utilities/iconResolver'
import { setCurrentStep, updateValue } from '../../onboardingSlice'
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks'
import { pushEvent } from '../../../../utilities/analytics'
import { WIZARD_HOTEL_PREF_ANSWERED, WIZARD_HOTEL_PREF_LOADED } from '../../../../constants/analyticEvents'
import FadeTransition from '../../../../components/animations/FadeTransition'
import { t } from '../../../../utilities/translator'
import Button from '../../../../components/button/Button'
import { createPortal } from 'react-dom'
import PersonalizationTag from '../../../../components/onboarding/PersonalizationTag'
import { transformPreferences } from './helpers'
import useUserPreferences from '../../../../hooks/preferences/useUserPreferences'

type Props = {
  showPreferences: (value: boolean) => void;
}

const HotelPreferencesStep = ({ showPreferences }: Props) => {
  const dispatch = useAppDispatch();
  const [active, setActive] = useState(false);
  const { dynamicStepsStatus, dynamicSteps } = useAppSelector(state => state.onboarding);
  const searchProfile = useAppSelector(state => state.user.searchProfile) || { id: 'general' };
  const facilities = useAppSelector((state: any) => state.secondaryFilter.data.facilities);
  const footerPortal = document.getElementById('wizard-next-button');
  const { user } = useAppSelector(state => state.user);
  const { updatePreference } = useUserPreferences(user?.muid || '')
  const skipRef = useRef(false);

  useEffect(() => {
    pushEvent(WIZARD_HOTEL_PREF_LOADED);
  }, [])

  useEffect(() => {
    if (dynamicStepsStatus === 'fulfilled' && dynamicSteps.length === 0) {
      if (skipRef.current) return;
      skipRef.current = true;
      setTimeout(() => {
        dispatch(setCurrentStep(1));
        dispatch(updateValue({ dynamicStepsStatus: 'idle' }));
      }, 100);
    }
  }, [dispatch, dynamicStepsStatus, dynamicSteps])

  const getUserPreferences = () => {
    const user = JSON.parse(localStorage.getItem('hf_user') || '{}');
    if (user.preferences) {
      return transformPreferences(user.preferences, searchProfile.id);
    }

    return null;
  }

  const userPreferences = getUserPreferences();

  useEffect(() => {
    if (userPreferences?.general && userPreferences.general.find(item => item.name === 'breakfast_quality')) {
      updatePreference({ breakfast_quality: true });
    }
  }, [])

  const onConfirm = () => {
    setActive(true);
    showPreferences(true);
    pushEvent(WIZARD_HOTEL_PREF_ANSWERED, { answer: 'Continue to hotel pref' });
    setTimeout(() => {
      dispatch(setCurrentStep(1));
    }, 500);
  }

  const onSkip = () => {
    showPreferences(false);
    pushEvent(WIZARD_HOTEL_PREF_ANSWERED, { answer: 'Skip to results' });
    setTimeout(() => {
      dispatch(setCurrentStep(1));
    }, 500);
  }

  const onChangePreferencesClick = () => {
    dispatch(updateValue({ showAllQuestions: true }));
    dispatch(updateValue({ changeAnswersClicked: true }));
    showPreferences(true);
    setTimeout(() => dispatch(setCurrentStep(1)), 300)
  }

  const onContinueClick = () => {
    dispatch(updateValue({ showAllQuestions: false }));
    dispatch(updateValue({ changeAnswersClicked: false }));
    showPreferences(true);
    setTimeout(() => dispatch(setCurrentStep(1)), 300)
  }

  if (dynamicStepsStatus === 'pending' || dynamicStepsStatus === 'idle') {
    return (
      <div className="wizard-step wizard-loading-step justify-center">
        <div className="wizard-splash__loader mt-10 self-center" />
      </div>
    )
  }

  const renderUserPreferences = () => {
    return (
      <div className="flex flex-wrap justify-center gap-2">
        {userPreferences && Object.keys(userPreferences).map(group => userPreferences[group].map(item => {
          if (item.name === 'facilities') {
            return Object.keys(item.facility_ids).map(facility => (
              <PersonalizationTag
                key={facility}
                text={facilities.find(item => +item.value === +facility)?.label?.name}
              />
            ))
          }

          return (
            <PersonalizationTag
              key={item.question_id}
              text={item.translation}
            />
          )
        }))}
      </div>
    )
  }

  return (
    <SwitchTransition mode="out-in">
      <FadeTransition key={dynamicStepsStatus} classNames='fade'>
        <div>
          {(dynamicStepsStatus === 'pending' || dynamicStepsStatus === 'idle') && (
            <div className="wizard-step wizard-loading-step justify-center">
              <div className="wizard-splash__loader mt-10 self-center" />
            </div>
          )}
          {dynamicStepsStatus === 'fulfilled' && dynamicSteps.length > 0 && (
            <>
              {(!userPreferences || Object.keys(userPreferences).length === 0) && (
                  <div className="wizard-step gap-10 wizard-question">
                    <div className="text-center">
                      <ReactSVG src={getImage('hotel-label.svg')} />
                    </div>
                    <div className="wizard-step__title">{t('onboarding.showQuestionsTitle')}</div>
                    <div>
                      <div className={cn('hotel-preferences-option', { active })} onClick={onConfirm}>
                        {t('onboarding.showQuestionsYes')}
                      </div>
                    </div>
                    <div className="text-center text-tertiary underline text-sm font-medium cursor-pointer" onClick={onSkip}>
                      {t('onboarding.showQuestionsNo')}
                    </div>
                  </div>
              )}

              {userPreferences && Object.keys(userPreferences).length > 0 && (
                <div className="wizard-step gap-10 wizard-question">
                  <div className="text-center">
                    <ReactSVG src={getImage('hotel-label.svg')} />
                  </div>
                  <div className="wizard-step__title">{t('onboarding.showQuestionsTitle')}</div>
                  <div>
                    <div className="text-center font-medium mb-4">{t('onboarding.lastFeedPreferences')}</div>
                    {renderUserPreferences()}
                  </div>

                  {footerPortal ? createPortal(
                    <div className="flex gap-3">
                      <Button label={t('onboarding.change')} className="w-full" type="secondary" size="lg" onClick={onChangePreferencesClick} />
                      <Button label={t('onboarding.continue')} className="w-full" type="primary" size="lg" onClick={onContinueClick} />
                    </div>,
                    footerPortal
                  ) : (
                    <div className="wizard-footer flex-row gap-3">
                      <Button label={t('onboarding.change')} className="w-full" type="secondary" size="lg" onClick={onChangePreferencesClick} />
                      <Button label={t('onboarding.continue')} className="w-full" type="primary" size="lg" onClick={onContinueClick} />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </FadeTransition>
    </SwitchTransition>
  )
}

export default HotelPreferencesStep
